import axios from 'axios';
import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../stores/useAuthStore';

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
});

// Queue management for concurrent 401s — prevents multiple simultaneous refresh calls
let isRefreshing = false;
let refreshQueue: Array<{ resolve: () => void; reject: (err: unknown) => void }> = [];

function processQueue(error: unknown): void {
  for (const item of refreshQueue) {
    if (error) {
      item.reject(error);
    } else {
      item.resolve();
    }
  }
  refreshQueue = [];
}

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: unknown) => {
    const axiosError = error as {
      response?: { status: number };
      config?: InternalAxiosRequestConfig & { _retried?: boolean };
    };

    const status = axiosError.response?.status;
    const config = axiosError.config;

    // Only attempt silent refresh once per request (AC3).
    // Never refresh for auth endpoints — a 401 from /auth/login is a real credential
    // failure, not an expired session. Intercepting it triggers a hard reload before
    // Login.tsx can display the error message.
    const isAuthEndpoint =
      config?.url === '/auth/login' ||
      config?.url === '/auth/refresh' ||
      config?.url?.includes('/auth/forgot-password') ||
      config?.url?.includes('/auth/reset-password') ||
      config?.url?.includes('/aspirant/signup');

    if (status !== 401 || !config || config._retried || isAuthEndpoint) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise<AxiosResponse>((resolve, reject) => {
        refreshQueue.push({
          resolve: () => {
            config._retried = true;
            resolve(apiClient(config));
          },
          reject,
        });
      });
    }

    isRefreshing = true;
    config._retried = true;

    try {
      await apiClient.post('/auth/refresh');
      processQueue(null);
      return apiClient(config);
    } catch (refreshError) {
      processQueue(refreshError);
      useAuthStore.getState().logout();
      window.location.href = '/login';
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export default apiClient;
